import { create } from "zustand";
import type { ActiveScope } from "@/lib/types";

/**
 * Active team/season scope — the foundation of every coach/feedback/admin
 * data fetch.
 *
 * The server is the source of truth: `/api/me` returns `active_scope`
 * (resolved + persisted) plus `selection_required` (true when the user
 * has multiple teams and hasn't picked one yet). The client only
 * mirrors that state for synchronous reads from non-React code (the
 * api fetch wrapper, query key builders).
 *
 * Mutations go through:
 *   - `setScope(teamId, seasonId)` — local-only, used during the
 *     selection flow before the PUT lands.
 *   - `applyMePayload(payload)` — called from the `useMe` hook after a
 *     successful `/api/me` GET; reconciles the store with whatever the
 *     server resolved (single source of truth).
 *
 * Why a Zustand store and not just a query selector:
 *   - `apiFetch` is called from outside React (TanStack Query's
 *     `queryFn`), so it can't use a hook. It reads scope directly via
 *     `useScopeStore.getState().teamId`.
 *   - The query key builder must produce the same key for two renders
 *     with the same scope, so it also reads from the store.
 *
 * Persistence: NOT persisted to localStorage. The server already
 * persists `users.last_team_id` / `users.last_season_id`; on next page
 * load `/api/me` returns it and `applyMePayload` rehydrates. One
 * source of truth.
 */

type ScopeState = {
  /** Active team id, or null if no team selected yet. */
  teamId: string | null;
  /** Active season id within the team, or null if the team has no
   * seasons or none selected. */
  seasonId: string | null;
  /** True when the user has multiple eligible teams and hasn't picked
   * one. Surfaces from `/api/me`'s `selection_required` flag. */
  selectionRequired: boolean;
  /** Last server-resolved scope, for non-id fields (team name, role,
   * capabilities) the UI might need synchronously. Null until /api/me
   * lands or after sign-out. */
  active: ActiveScope | null;

  /** Optimistic local update — call when the user picks a scope in the
   * UI. The actual PUT /api/me/scope must follow; on its response,
   * `applyMePayload` will reconcile. */
  setScope: (teamId: string | null, seasonId: string | null) => void;

  /** Reconcile store from a /api/me payload (the only authoritative
   * scope source). Always preferred over `setScope` after a fetch. */
  applyMePayload: (payload: {
    active_scope: ActiveScope | null;
    selection_required: boolean;
  }) => void;

  /** Wipe state on sign-out. */
  clear: () => void;
};

export const useScopeStore = create<ScopeState>((set) => ({
  teamId: null,
  seasonId: null,
  selectionRequired: false,
  active: null,

  setScope: (teamId, seasonId) =>
    set({
      teamId: teamId || null,
      seasonId: seasonId || null,
    }),

  applyMePayload: (payload) =>
    set({
      teamId: payload.active_scope?.team.id ?? null,
      seasonId: payload.active_scope?.season?.id ?? null,
      selectionRequired: payload.selection_required,
      active: payload.active_scope,
    }),

  clear: () =>
    set({
      teamId: null,
      seasonId: null,
      selectionRequired: false,
      active: null,
    }),
}));

/** Non-hook accessor for code outside React (e.g. `apiFetch`, query key
 * builders). Returns the current scope snapshot. */
export function currentScope(): { teamId: string | null; seasonId: string | null } {
  const s = useScopeStore.getState();
  return { teamId: s.teamId, seasonId: s.seasonId };
}
